import {
  AdminPermissionsSystemValues,
  ApplicationPermissionsSystemValues,
  Security,
  SecurityGroupAssignment,
  SecurityV2,
} from '@fluentsms/types-fluent';
import { PROD, resolveDomain, resolveEnv, TITLESOLUTIONS } from '@fluentsms/utils';
import jwtDecode from 'jwt-decode';
import memoize from 'lodash/memoize';
import React, {
  createContext,
  ReactElement,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { gunzipSync } from 'zlib';
import client, { AuthClient } from './client';
import config from './config';
import { Auth0ErrorCode } from './enums/Auth0ErrorCode';
import { Authenticator } from './models/authenticator';
import tokenUtility, { TokenUtility } from './token';

export type AuthContextType = {
  isAuthenticated: () => boolean;
  isAuthenticating: () => boolean;
  signIn: (...args: string[]) => Promise<void>;
  signOut: (...args: string[]) => Promise<void>;
  readonly isSigningOut: boolean;
  isAuthorized: (
    authorizationTarget: AdminPermissionsSystemValues | ApplicationPermissionsSystemValues,
    tenantOrgEntityId?: string,
  ) => boolean;
  hasAuthorizedAnywhere: (
    authorizationTarget: AdminPermissionsSystemValues | ApplicationPermissionsSystemValues,
  ) => boolean;
  hasAdminAuthorizations: boolean;
  userAccessibleCompanies: string[];
  getAuthenticators: () => Promise<Authenticator[]>;
  authorizeMfa: () => void;
  setMfaToken: () => Promise<void>;
  getMfaActionQueryParam: () => { key: string; value: string; combined: string };
  renewTokens: (params?: { onSuccess?: () => void; onError?: (error: Error) => void }) => Promise<void>;
  /**
   * This function allows you to add a callback that will be called when the tokens are renewed by ANY consumer of the AuthProvider.
   */
  addGlobalRenewTokenCallback: (callback: () => void) => void;
  tokenUtility: TokenUtility;
  client: AuthClient;
  updateAuthorizations: () => void;
  logoutChannel: BroadcastChannel;
  tokenRenewedChannel: BroadcastChannel;
  getAdminPermissions: () => string[];
  getCompanyApplicationPermissions: (companyId: string) => ApplicationPermissionsSystemValues[];
  getTenantId: () => string;
  switchTenant: (selectedTenantId?: string) => Promise<void>;
};

export type ProviderProps = {
  children: ReactNode;
};

export type LoadingDecoratorCallbackType = (...args: string[]) => Promise<Error | null | void>;

export const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export function AuthProvider({ children }: ProviderProps): ReactElement {
  const auth = useProviderAuth();

  return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>;
}

const PUBLIC_PATHS = ['/select-tenant'];

export function useAuth(): AuthContextType {
  return useContext(AuthContext);
}

function useProviderAuth(): AuthContextType {
  const [isLoading, setIsLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const [authorizations, setAuthorizations] = useState<Record<string, string[]> | boolean>(false);
  const [securityV2, setSecurityV2] = useState<SecurityV2 | null>(null);
  const [isSigningOut, setIsSigningOut] = useState(false);
  const [onGlobalRenewTokensCallbacks, setOnGlobalRenewTokensCallbacks] = useState<(() => void)[]>([]);
  const [logoutChannel] = useState(() => new BroadcastChannel('logout'));
  const [tenantSwitchChannel] = useState(() => new BroadcastChannel('tenant-switch'));
  const [tokenRenewedChannel] = useState(() => new BroadcastChannel('token-renewed'));
  const redirectHandled = useRef(false);

  const userAccessibleCompanies = securityV2?.companies ?? [];

  const loadingDecorator = (callback: LoadingDecoratorCallbackType) => {
    return async (...args: string[]) => {
      setIsLoading(true);
      await callback(...args);
      setIsLoading(false);
    };
  };

  const signIn = loadingDecorator(async (): Promise<void> => {
    try {
      const accessToken = await client.getTokenSilently();
      const idTokenRaw = await client.getIdTokenClaims();
      if (accessToken && idTokenRaw) {
        tokenUtility.cacheTokens(accessToken, idTokenRaw);
      }
    } catch (error: unknown) {
      const err = error as { error?: string };
      switch (err.error) {
        case Auth0ErrorCode.LOGIN_REQUIRED:
        case Auth0ErrorCode.INVALID_TOKEN:
        case Auth0ErrorCode.CONSENT_REQUIRED:
        case Auth0ErrorCode.MISSING_REFRESH_TOKEN:
        case Auth0ErrorCode.INTERACTION_REQUIRED:
          await client.authorize();
          break;
        default:
          console.error('Unhandled auth error:', error);
      }
    }
  });

  const renewTokens = async (params?: { onSuccess?: () => void; onError?: (error: Error) => void }): Promise<void> => {
    try {
      const accessToken = await client.getTokenSilently({ cacheMode: 'off' });
      const idTokenRaw = await client.getIdTokenClaims();
      if (accessToken && idTokenRaw) {
        tokenUtility.cacheTokens(accessToken, idTokenRaw);
      }
      onGlobalRenewTokensCallbacks.forEach((callback) => callback());
      params?.onSuccess?.();
    } catch (error) {
      params?.onError?.(error as Error);
    }
  };

  const authorizeMfa = (): void => {
    client.authorizeMfa();
  };

  const setMfaToken = async (): Promise<void> => {
    const accessToken = await client.getTokenSilently();
    if (accessToken) {
      tokenUtility.cacheMfaToken(accessToken);
    }
  };

  const getAuthenticators = async (): Promise<Authenticator[]> => {
    const response = (await (
      await fetch(`${config.mfaUrl}authenticators`, {
        headers: { Authorization: `Bearer ${tokenUtility.getMfaToken()}` },
      })
    ).json()) as { id: string; authenticator_type: string; active: boolean }[];

    return response.map((authenticator) => ({
      id: authenticator.id,
      type: authenticator.authenticator_type,
      active: authenticator.active,
    }));
  };

  const addGlobalRenewTokenCallback = (callback: () => void): void => {
    setOnGlobalRenewTokensCallbacks((callbacks) => [...callbacks, callback]);
  };

  /**
   * DO NOT set isSigningOut to false here or anywhere else.
   * There is a race condition where, upon logout, auth0 redirects to the login, which attempts to check the session.
   * This checkSession and logout conflict with each other and can cause strange behavior, such as a silent auth, which
   * might result in the user being unexpectedly logged back in.
   */
  const signOut = loadingDecorator(async () => {
    setIsSigningOut(true); // DO NOT set this to false. See comment above
    tokenUtility.clearStorage();
    await client.signOut();
  });

  const isAuthenticated = (): boolean => {
    const accessToken = tokenUtility.getAccessToken();
    const idToken = tokenUtility.getIdToken();

    const hasValidAccessToken = tokenUtility.validateToken(accessToken);
    const hasValidIdToken = tokenUtility.validateToken(idToken);

    return hasValidAccessToken && hasValidIdToken;
  };

  const securityV2IsAuthorized = (
    authorizationTarget: AdminPermissionsSystemValues | ApplicationPermissionsSystemValues,
    orgEntityId: string,
    viewDirectoryManagerUniversal: boolean,
  ): boolean => {
    if (viewDirectoryManagerUniversal) {
      if (securityV2?.applicationPermissions[authorizationTarget]) {
        return true;
      }
    } else {
      if (securityV2) {
        if (securityV2?.adminPermissions.includes(authorizationTarget)) {
          return true;
        }

        if (!securityV2?.applicationPermissions[authorizationTarget]) {
          return false;
        }

        let companyIndex;
        if (securityV2?.companies.includes(orgEntityId)) {
          companyIndex = securityV2?.companies.indexOf(orgEntityId);
        } else return false;

        return securityV2?.applicationPermissions[authorizationTarget].includes(companyIndex);
      } else {
        return false;
      }
    }
    return false;
  };

  const parseAuthorizations = useCallback(() => {
    const authorizationBlock: Record<string, string[]> = {};
    const decodedToken = tokenUtility.getDecodedDetailToken();

    if (decodedToken.securityVersion === 'V2') {
      const securityV2: SecurityV2 = JSON.parse(gunzipSync(Buffer.from(decodedToken.securityV2, 'base64')).toString());
      setSecurityV2(securityV2);
    } else {
      const security: Security = (decodedToken.security as unknown as Security) ?? [];
      const securityAssignmentList: SecurityGroupAssignment[] = security?.securityAssignments ?? [];

      for (const securityBlock of securityAssignmentList) {
        let permissionBlock: string[] = [];
        const orgIds = securityBlock.orgIds ?? [];
        const groupList = securityBlock.securityGroups ?? [];
        for (const list of groupList) {
          permissionBlock = [...permissionBlock, ...Object.keys(list.permissions)];
        }
        for (const orgId of orgIds) {
          authorizationBlock[orgId] = permissionBlock;
        }
      }
    }

    setAuthorizations(authorizationBlock);
  }, []);

  const isAuthorizedUnMemoized = (
    authorizationTarget: AdminPermissionsSystemValues | ApplicationPermissionsSystemValues,
    tenantOrgEntityId = '',
  ) => {
    const decodedToken = tokenUtility.getDecodedDetailToken();
    let tenantID = tenantOrgEntityId;
    const isSecurityV2 = decodedToken.securityVersion === 'V2';
    if (tenantOrgEntityId === '') {
      tenantID = decodedToken['tenantId'];
    }

    if (isSecurityV2) {
      return securityV2IsAuthorized(authorizationTarget, tenantID, false);
    }

    if (
      typeof authorizations !== 'boolean' &&
      authorizations[tenantID] &&
      authorizations[tenantID].includes(authorizationTarget)
    ) {
      return true;
    }
    return false;
  };

  const isAuthorizedMemoResolver = (
    authorizationTarget: AdminPermissionsSystemValues | ApplicationPermissionsSystemValues,
    tenantOrgEntityId = '',
  ) => JSON.stringify([authorizationTarget, tenantOrgEntityId]);

  const isAuthorized = memoize(isAuthorizedUnMemoized, isAuthorizedMemoResolver);

  const hasAuthorizedAnywhere = useCallback(
    (authorizationTarget: AdminPermissionsSystemValues | ApplicationPermissionsSystemValues) => {
      const decodedToken = tokenUtility.getDecodedDetailToken();
      const isSecurityV2 = decodedToken.securityVersion === 'V2';

      if (isSecurityV2) {
        const tenantID = decodedToken['tenantId'];
        return securityV2IsAuthorized(authorizationTarget, tenantID, true);
      } else {
        if (typeof authorizations !== 'boolean') {
          for (const iter in authorizations) {
            if (authorizations[iter] && authorizations[iter].includes(authorizationTarget)) {
              return true;
            }
          }
        }
      }

      return false;
    },
    [authorizations],
  );

  const isAuthenticating = () => {
    return !isInitialized || isLoading;
  };

  // NOTE: This is a temporary solution to determine if the user has admin authorizations while
  // "accessDocuments" is still the only selectable access permission. defaults are set to access true
  const hasAdminAuthorizations = securityV2
    ? securityV2?.adminPermissions.filter((permission) => !permission.includes('access')).length > 0
    : false;

  const updateAuthorizations = useCallback((): void => {
    parseAuthorizations();
  }, [parseAuthorizations]);

  useEffect(() => {
    const initialize = async () => {
      setIsLoading(true);
      let wasRedirectCallback = false;
      try {
        const searchParams = new URLSearchParams(window.location.search);
        if (searchParams.has('code') && searchParams.has('state')) {
          if (!redirectHandled.current) {
            redirectHandled.current = true;
            wasRedirectCallback = true;
            await client.handleRedirectCallback();
            const pathname = window.location.pathname;
            const safePath = /^[/a-zA-Z0-9\-._~!$&'()*+,;=:@%]+$/.test(pathname) ? pathname : '/';
            window.history.replaceState({}, '', safePath);
          }
        }

        if (!PUBLIC_PATHS.includes(window.location.pathname)) {
          const accessToken = await client.getTokenSilently();
          const idTokenRaw = await client.getIdTokenClaims();
          if (accessToken && idTokenRaw) {
            tokenUtility.cacheTokens(accessToken, idTokenRaw);
            const newTenantId = tokenUtility.getDecodedDetailToken()?.tenantId ?? '';
            if (newTenantId) {
              const storedTenantId = localStorage.getItem('fluent.activeTenantId');
              if (wasRedirectCallback && storedTenantId !== null && storedTenantId !== newTenantId) {
                tenantSwitchChannel.postMessage('tenant-switched');
              }
              localStorage.setItem('fluent.activeTenantId', newTenantId);
            }
          }
        }
      } catch (error: unknown) {
        const err = error as { error?: string };
        if (
          err.error !== Auth0ErrorCode.LOGIN_REQUIRED &&
          err.error !== Auth0ErrorCode.CONSENT_REQUIRED &&
          err.error !== Auth0ErrorCode.MISSING_REFRESH_TOKEN &&
          err.error !== Auth0ErrorCode.INTERACTION_REQUIRED
        ) {
          console.error('[AuthProvider] Initialization error:', error);
        }
      }
      setIsInitialized(true);
      setIsLoading(false);
    };

    initialize();
  }, [tenantSwitchChannel]);

  useEffect(() => {
    if (!authorizations && isInitialized && !isLoading && isAuthenticated()) {
      updateAuthorizations();
    }
  }, [authorizations, isInitialized, isLoading, updateAuthorizations]);

  useEffect(() => {
    return () => {
      logoutChannel.close();
      tenantSwitchChannel.close();
      tokenRenewedChannel.close();
    };
  }, [logoutChannel, tenantSwitchChannel, tokenRenewedChannel]);

  logoutChannel.onmessage = async (e: MessageEvent) => {
    if (e.data === 'logout') {
      await signOut();
    }
  };

  tenantSwitchChannel.onmessage = (e: MessageEvent) => {
    if (e.data === 'tenant-switched') {
      window.location.replace('/');
    }
  };

  tokenRenewedChannel.onmessage = (e: MessageEvent) => {
    if (e.data === 'token-renewed') {
      renewTokens({
        onSuccess: () => updateAuthorizations(),
      });
    }
  };

  const getAdminPermissions = (): string[] => {
    if (!securityV2 || !hasAdminAuthorizations) {
      return [];
    }

    return securityV2.adminPermissions;
  };

  const getCompanyApplicationPermissions = (companyId: string): ApplicationPermissionsSystemValues[] => {
    if (!securityV2) {
      return [];
    }

    const companyIndex = securityV2.companies.indexOf(companyId);

    if (companyIndex === -1) {
      return [];
    }

    const permissions: ApplicationPermissionsSystemValues[] = [];

    for (const [permissionKey, companyIndices] of Object.entries(securityV2.applicationPermissions)) {
      if (Array.isArray(companyIndices) && companyIndices.includes(companyIndex)) {
        permissions.push(permissionKey as ApplicationPermissionsSystemValues);
      }
    }

    return permissions;
  };

  const getTenantId = (): string => {
    try {
      const token = tokenUtility.getIdToken();
      if (!token) return '';

      const decodedToken: Record<string, { tenantId: string }> = jwtDecode(token);
      const env = resolveEnv() === PROD ? '' : `${resolveEnv().toLowerCase()}.`;
      const domain =
        resolveDomain() === TITLESOLUTIONS ? `fluentapi.${resolveDomain()}.com` : `api.${resolveDomain()}.com`;

      const tenantId = decodedToken[`https://${env}${domain}/users/auth`]?.tenantId ?? '';

      return tenantId;
    } catch (error) {
      return '';
    }
  };

  return {
    isAuthenticated,
    isAuthenticating,
    signIn,
    signOut,
    isSigningOut,
    isAuthorized,
    hasAuthorizedAnywhere,
    hasAdminAuthorizations,
    userAccessibleCompanies,
    getAuthenticators,
    authorizeMfa,
    setMfaToken,
    getMfaActionQueryParam: client.getMfaActionQueryParam,
    tokenUtility,
    client,
    renewTokens,
    addGlobalRenewTokenCallback,
    updateAuthorizations,
    logoutChannel,
    tokenRenewedChannel,
    getAdminPermissions,
    getCompanyApplicationPermissions,
    getTenantId,
    switchTenant: client.switchTenant.bind(client),
  };
}
